import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProfileSidebarComponent } from '../../../components/profile-sidebar/profile-sidebar.component';
import { LoaderComponent } from '../../../components/loader/loader.component';

export interface CodexProtocol {
  id: string;
  roman: string;
  category: 'account' | 'cashier' | 'fairness' | 'legal';
  title: string;
  badge: string;
  summary: string;
  details: string[];
  auditBadge: string;
  icon: string;
}

@Component({
  selector: 'app-terms-and-conditions',
  standalone: true,
  imports: [CommonModule, FormsModule, ProfileSidebarComponent, LoaderComponent],
  templateUrl: './terms-and-conditions.component.html',
  styleUrls: ['./terms-and-conditions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermsAndConditionsComponent {
  activeCategory: 'all' | 'account' | 'cashier' | 'fairness' | 'legal' = 'all';
  searchTerm: string = '';
  expandedProtocolId: string | null = null;

  categories = [
    { key: 'all', label: 'All Protocols', icon: 'fas fa-layer-group' },
    { key: 'account', label: 'Account Sovereignty', icon: 'fas fa-user-shield' },
    { key: 'cashier', label: 'Cashier & Settlements', icon: 'fas fa-vault' },
    { key: 'fairness', label: 'Provably Fair & RNG', icon: 'fas fa-dice' },
    { key: 'legal', label: 'Compliance & Rights', icon: 'fas fa-scale-balanced' },
  ];

  protocols: CodexProtocol[] = [
    {
      id: 'p1',
      roman: 'PROTOCOL I',
      category: 'legal',
      title: 'Citizen Eligibility & Legal Majority',
      badge: '18+ MANDATORY',
      summary: 'Strict jurisdictional qualification and chronological age requirements governing casino access.',
      details: [
        'Participation on the Luckycrest sovereign platform is exclusively permitted to verified citizens who have achieved at least 18 years of age, or the legal age of majority applicable in their jurisdiction.',
        'Creation of accounts by or on behalf of minors is strictly prohibited. Any balances or winnings originating from ineligible accounts are summarily nullified and forfeited.'
      ],
      auditBadge: 'Biometric DOB & Identity Audit Enforced',
      icon: 'fas fa-id-card'
    },
    {
      id: 'p2',
      roman: 'PROTOCOL II',
      category: 'account',
      title: 'Vault Security & Credential Protection',
      badge: 'VAULT CIPHER',
      summary: 'Mandatory credential isolation, session hygiene, and private access key maintenance.',
      details: [
        'Registered citizens hold exclusive and non-delegable responsibility for guarding their vault credentials, PINs, and two-factor tokens.',
        'Luckycrest staff will never request plaintext passwords. All operations initiated under authenticated access credentials are deemed authoritatively executed by the verified account owner.'
      ],
      auditBadge: '256-Bit SSL Military Grade Encryption',
      icon: 'fas fa-key'
    },
    {
      id: 'p3',
      roman: 'PROTOCOL III',
      category: 'cashier',
      title: 'Cashier Deposits, Settlements & Payouts',
      badge: 'INSTANT LEDGER',
      summary: 'Multi-rail liquidity protocols, wager turnover rules, and automated redemption processing.',
      details: [
        'Deposit funding operations are credited in real time upon confirmation on the respective payment network or blockchain.',
        'Redemption requests are processed through automated risk pipelines. Accounts must fulfill minimum wagering turnover criteria prior to settlement distribution.',
        'Daily, weekly, and monthly redemption limits calibrate dynamically in accordance with player VIP tier status.'
      ],
      auditBadge: 'Automated Cashier Risk Clearance',
      icon: 'fas fa-hand-holding-dollar'
    },
    {
      id: 'p4',
      roman: 'PROTOCOL IV',
      category: 'fairness',
      title: 'Provably Fair Gaming & Certified RNG',
      badge: 'PROVABLY FAIR',
      summary: 'Cryptographic hash verifications and independent mathematical randomness compliance.',
      details: [
        'Every game outcome produced across slots, table games, and live modules utilizes cryptographically secure, certified Random Number Generators (RNG).',
        'Outcomes are tamper-proof and verifiable against pre-committed server and client seed pairs to ensure absolute transparency and mathematical integrity.'
      ],
      auditBadge: 'Independent RNG Lab Certification',
      icon: 'fas fa-dice-d20'
    },
    {
      id: 'p5',
      roman: 'PROTOCOL V',
      category: 'account',
      title: 'Single Account Policy & Anti-Sybil Defense',
      badge: 'UNIQUE CITIZEN',
      summary: 'Prohibition against duplicate identity registration, bonus abuse, and collusion rings.',
      details: [
        'Each citizen is entitled to precisely one verified personal gaming vault. Multi-accounting, proxy registration, and syndicate operations are strictly tracked.',
        'Detection of multi-accounting warrants instant ledger freezes, cancellation of promotional incentives, and permanent blacklist sanctions.'
      ],
      auditBadge: 'Device Fingerprint & IP Telemetry Guard',
      icon: 'fas fa-fingerprint'
    },
    {
      id: 'p6',
      roman: 'PROTOCOL VI',
      category: 'legal',
      title: 'Responsible Gaming & Player Safeguards',
      badge: 'SOVEREIGN CARE',
      summary: 'Self-exclusion instruments, voluntary cool-off horizons, and custom liquidity limits.',
      details: [
        'Luckycrest empowers players with comprehensive responsible gaming switches: custom deposit ceilings, voluntary session timeouts, and self-exclusion periods.',
        'Self-exclusion orders take effect immediately upon request and remain irreversible until the full expiration of the chosen horizon.'
      ],
      auditBadge: 'Instant Self-Exclusion Lockout Protocol',
      icon: 'fas fa-heart-pulse'
    }
  ];

  constructor(private location: Location) {}

  CloseModal() {
    this.location.back();
  }

  setCategory(cat: any) {
    this.activeCategory = cat;
  }

  toggleExpand(id: string) {
    this.expandedProtocolId = this.expandedProtocolId === id ? null : id;
  }

  get filteredProtocols(): CodexProtocol[] {
    return this.protocols.filter(p => {
      const matchesCategory = this.activeCategory === 'all' || p.category === this.activeCategory;
      const term = this.searchTerm.toLowerCase().trim();
      const matchesSearch = !term ||
        p.title.toLowerCase().includes(term) ||
        p.summary.toLowerCase().includes(term) ||
        p.roman.toLowerCase().includes(term);
      return matchesCategory && matchesSearch;
    });
  }

  get userDetails() {
    return {
      name: localStorage.getItem('userName'),
      email: localStorage.getItem('email'),
      kycStatus: localStorage.getItem('KYC'),
    };
  }
}
